'use client'

import { ETrackingEvent } from '@/lib/gtm/type'
import { generateUUID } from '@/lib/utils/crypto'
import { sendGTMEvent } from '@next/third-parties/google'
import debounce from 'lodash.debounce'
import { useLocale } from 'next-intl'
import { useEffect, useMemo } from 'react'
import { useIsMobileClient } from '../common/use-device-type'

export function useGATracking(useGa: boolean) {
  const isMobile = useIsMobileClient()
  const locale = useLocale()

  // Chỉ debounce phần "gửi GA", không debounce cả MouseEvent
  const debouncedSend = useMemo(
    () =>
      debounce(
        (payload: {
          value: string
          label?: string | null
          isMobile: boolean
          locale: string
        }) => {
          const { value, label, isMobile, locale } = payload

          // log để debug
          console.info(
            '%cclick_tracking %c%s %clabel: %s %cisMobile: %s',
            'color: #2563eb; font-weight: bold;',
            'color: #f59e42; font-weight: bold; font-size: 18px;',
            `${isMobile ? 'mb' : ''}${value}`.toLowerCase(),
            'color: #a21caf; font-weight: bold;',
            label ?? '',
            'color: #059669; font-weight: bold;',
            isMobile
          )

          try {
            sendGTMEvent({
              event: ETrackingEvent.ClickTracking, // GA4 event name
              event_category: 'click_tracking',
              event_label: label ?? '',
              event_value: 1,
              link_class: `${isMobile ? 'mb' : ''}${value}`.toLowerCase(),
              transport_type: 'beacon',
              frontendversion: locale,
              unique_event_id: generateUUID(),
            })
          } catch (error) {
            console.error('Error calling sendGTMEvent:', error)
          }
        },
        250 // nhạy vừa đủ, tránh spam
      ),
    []
  )

  useEffect(() => {
    if (!useGa) return

    const handleClick = (e: MouseEvent) => {
      // KHÔNG preventDefault / stopPropagation — để app hoạt động bình thường

      // e.target có thể là Text/SVG -> chuẩn hoá thành Element
      const node = e.target as Node | null
      const startEl =
        (node instanceof Element ? node : node?.parentElement) ?? null
      if (!startEl) return

      const trackingEl = startEl.closest<HTMLElement>('[ga-tracking-value]')
      if (!trackingEl) return

      const value = trackingEl.getAttribute('ga-tracking-value')
      if (!value) return

      const label = trackingEl.getAttribute('ga-tracking-label')

      // Gửi qua debounce chỉ với payload đã “rút gọn”
      debouncedSend({
        value,
        label,
        isMobile,
        locale,
      })
    }

    // Bubble phase (mặc định). Thêm passive để chắc chắn không chặn default.
    document.addEventListener('click', handleClick, { passive: true })

    return () => {
      document.removeEventListener('click', handleClick, {
        passive: true,
      } as EventListenerOptions)
      debouncedSend.cancel()
    }
  }, [isMobile, useGa, debouncedSend, locale])
}
